import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Spinner } from "@/shared/components/ui/spinner";
import { useProfile } from "@/features/profile/services";
import { useMembershipStatus } from "@/features/plan/services";
import { getKycState } from "@/features/kyc/utils/kyc-state";
import { resolveRouteForProfileMembership } from "@/features/plan/lib/membership-flow";

type OnboardingGuardProps = {
  children: ReactElement;
};

const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const guardsDisabled =
    import.meta.env.VITE_DISABLE_GUARDS?.toString().toLowerCase() === "true";
  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useProfile({
    staleTime: 30_000,
    retry: 1,
  });

  const {
    data: membershipStatus,
    isLoading: isMembershipLoading,
    isError: isMembershipError,
    error: membershipError,
  } = useMembershipStatus({
    staleTime: 30_000,
    retry: false,
  });

  if (guardsDisabled) {
    return children;
  }

  if (isProfileLoading || isMembershipLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-white/70">
          <Spinner className="size-5" />
          Preparing your workspace...
        </div>
      </div>
    );
  }

  const membershipPlanName = profile?.membership?.planName;
  const membershipStatusValue = membershipStatus?.status?.toUpperCase();
  const kycState = getKycState(
    profile?.kyc?.status ?? null,
    profile?.kyc?.attemptStatus ?? null
  );

  const membershipApiError = membershipError ?? undefined;
  const membershipNotFound =
    membershipApiError?.errorName === "membership-not-found" ||
    membershipApiError?.errorCode === "404103";

  const hasMembershipPlan = Boolean(membershipPlanName);
  const hasActiveMembership = membershipStatusValue === "ACTIVE";
  const profileMembershipRoute = resolveRouteForProfileMembership(
    profile?.membership ?? null
  );

  if (profileMembershipRoute) {
    return <Navigate to={profileMembershipRoute} replace />;
  }

  if (
    membershipNotFound ||
    !hasMembershipPlan ||
    !hasActiveMembership ||
    isMembershipError
  ) {
    return <Navigate to="/plan/membership" replace />;
  }

  if (kycState === "rejected") {
    return <Navigate to="/kyc-verification-rejected" replace />;
  }

  if (kycState === "pending") {
    return <Navigate to="/kyc-verification-pending" replace />;
  }

  if (kycState !== "verified") {
    return <Navigate to="/kyc-verification" replace />;
  }

  if (isProfileError) {
    return <Navigate to="/plan/membership" replace />;
  }

  return children;
};

export default OnboardingGuard;
